// src/dados.js
// Listas compartilhadas entre as telas (empresas, usuários, consultores, módulos,
// pendências abertas e concluídas), sempre atualizadas:
//  - cada lista é buscada na API uma vez, quando alguma tela precisa dela;
//  - um canal de eventos avisa quando algo muda no servidor e só a lista afetada é recarregada.
import { useEffect, useState } from 'react';
import { api, API_BASE, obterToken, avisarBloqueio } from './api';

const ROTAS = {
    empresas: '/empresas',
    usuarios: '/usuarios',
    consultores: '/consultores',
    modulos: '/modulos',
    pendencias: '/pendencias?situacao=abertas',
    concluidas: '/pendencias?situacao=concluidas',
};
// qual aviso do servidor recarrega qual lista
const AFETA = { pendencias: ['pendencias', 'concluidas'] };

const VAZIO = [];
const estado = {};      // nome -> { dados, carregado, erro, ativo, buscando, pendente }
const ouvintes = {};    // nome -> Set(funções)

const avisar = (nome) => (ouvintes[nome] || new Set()).forEach((fn) => fn());

const buscar = async (nome) => {
    const e = estado[nome];
    if (e.buscando) { e.pendente = true; return; }
    e.buscando = true;
    try {
        e.dados = await api.get(ROTAS[nome]);
        e.erro = null;
    } catch (err) {
        console.error(`Erro ao carregar ${nome}:`, err);
        e.erro = err;
    } finally {
        e.carregado = true;
        e.buscando = false;
        avisar(nome);
        if (e.pendente) { e.pendente = false; buscar(nome); }
    }
};

// recarrega uma lista agora (usado depois de gravar, para a tela atualizar na hora)
export const recarregar = (nome) => Promise.all(
    (AFETA[nome] || [nome]).map((n) => (estado[n] && estado[n].ativo ? buscar(n) : null))
);

// ---------- canal de eventos (tempo real) ----------
let fonte = null;
let reconectar = null;
let parado = false;

const abrirCanal = async () => {
    if (fonte || parado) return;
    try {
        const token = await obterToken();
        fonte = new EventSource(`${API_BASE}/api/eventos?token=${encodeURIComponent(token)}`);
        fonte.addEventListener('mudanca', (ev) => {
            try { recarregar(JSON.parse(ev.data).colecao); } catch (e) { /* ignora */ }
        });
        // o consultor logado foi inativado: o servidor avisa e encerra a conexão
        fonte.addEventListener('bloqueado', (ev) => {
            let msg;
            try { msg = JSON.parse(ev.data).erro; } catch (e) { /* ignora */ }
            parado = true;
            if (fonte) fonte.close();
            fonte = null;
            avisarBloqueio(msg);
        });
        fonte.onerror = () => {
            // o token expira a cada hora: fecha e reabre com um token novo
            if (fonte) fonte.close();
            fonte = null;
            clearTimeout(reconectar);
            reconectar = setTimeout(() => {
                abrirCanal();
                // ao voltar, atualiza tudo o que estiver em uso (pode ter mudado enquanto caiu)
                Object.keys(estado).forEach((n) => { if (estado[n].ativo) buscar(n); });
            }, 3000);
        };
    } catch (e) {
        clearTimeout(reconectar);
        reconectar = setTimeout(abrirCanal, 5000);
    }
};

// encerra tudo (logout)
export const encerrarDados = () => {
    parado = true;
    clearTimeout(reconectar);
    if (fonte) fonte.close();
    fonte = null;
    Object.keys(estado).forEach((n) => { delete estado[n]; });
};
export const iniciarDados = () => { parado = false; };

// ---------- hook ----------
// const { dados, carregando, erro } = useColecao('empresas')
// habilitado = false: não busca nada (ex.: antes do login)
export function useColecao(nome, habilitado = true) {
    const [, forcar] = useState(0);
    useEffect(() => {
        if (!habilitado) return undefined;
        const fn = () => forcar((n) => n + 1);
        ouvintes[nome] = ouvintes[nome] || new Set();
        ouvintes[nome].add(fn);
        if (!estado[nome]) estado[nome] = { dados: [], carregado: false, erro: null, ativo: true };
        if (!estado[nome].carregado && !estado[nome].buscando) buscar(nome);
        if (!parado) abrirCanal();
        return () => { ouvintes[nome].delete(fn); };
    }, [nome, habilitado]);
    const e = (habilitado && estado[nome]) || { dados: VAZIO, carregado: false, erro: null };
    return { dados: e.dados, carregando: !e.carregado, erro: e.erro };
}
